import React from 'react'

import { ArrowDropDown, ArrowDropUp, KeyboardReturn as KeyboardReturnIcon } from '@mui/icons-material'
import {
    Box,
    ButtonBase,
    CircularProgress,
    ClickAwayListener,
    FormControl,
    List,
    ListItemButton,
    Paper,
    Popper,
    TextField,
} from '@mui/material'

import { getOptionLabel } from './UserAccountSelect.utils'
import { UserAccountSelectProps } from './UserAccountSelect.types'
import AuctionOptionContent from './AuctionOptionContent'
import { useUserAccountSelectController } from './useUserAccountSelectController'

const UserAccountSelect: React.FC<UserAccountSelectProps> = ({
    activeOrganization,
    organizations,
    isGlobalAdmin,
    handleClose,
    openRequest,
}: UserAccountSelectProps) => {
    const {
        auctionList,
        closeDropdown,
        filteredAuctionList,
        handleOrgChange,
        handleSearchEnter,
        handleSearchTab,
        handleTriggerKeyDown,
        isOpen,
        isSwitching,
        openDropdown,
        searchInputRef,
        searchValue,
        selectedAuction,
        selectedAuctionId,
        setSearchValue,
        triggerButtonRef,
    } = useUserAccountSelectController({
        activeOrganization,
        organizations,
        openRequest,
        handleClose,
    })

    return (
        <ClickAwayListener
            onClickAway={() => {
                if (isOpen) {
                    if (isSwitching) {
                        return
                    }

                    closeDropdown()
                }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, width: '100%' }}>
                <FormControl
                    sx={{
                        maxWidth: '100%',
                        minWidth: 0,
                        width: {
                            xs: '100%',
                            sm: 'clamp(220px, 26vw, 360px)',
                            md: 'clamp(260px, 30vw, 440px)',
                        },
                    }}
                >
                    <ButtonBase
                        ref={triggerButtonRef}
                        aria-expanded={isOpen}
                        aria-haspopup="listbox"
                        aria-label={
                            selectedAuction ? `Selected auction ${getOptionLabel(selectedAuction)}` : 'Select auction'
                        }
                        disabled={auctionList.length < 2 || isSwitching}
                        onClick={() => {
                            if (isOpen) {
                                closeDropdown()
                                return
                            }

                            openDropdown()
                        }}
                        onKeyDown={handleTriggerKeyDown}
                        sx={{
                            alignItems: 'center',
                            color: selectedAuction ? 'primary.main' : 'text.secondary',
                            display: 'flex',
                            justifyContent: 'space-between',
                            minHeight: 44,
                            textAlign: 'left',
                            width: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                flex: 1,
                                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                minWidth: 0,
                                pr: 1.5,
                                typography: 'body1',
                            }}
                        >
                            {selectedAuction ? (
                                <AuctionOptionContent
                                    organization={selectedAuction}
                                    isGlobalAdmin={isGlobalAdmin}
                                    isSelected
                                />
                            ) : (
                                'Select auction'
                            )}
                        </Box>
                        {isOpen ? <ArrowDropUp /> : <ArrowDropDown />}
                    </ButtonBase>
                    <Popper
                        anchorEl={triggerButtonRef.current}
                        open={isOpen}
                        placement="bottom-end"
                        sx={{ zIndex: (theme) => theme.zIndex.modal }}
                    >
                        <Paper elevation={8} sx={{ mt: 1 }}>
                            <Box
                                sx={{
                                    bgcolor: 'background.paper',
                                    px: 1.5,
                                    pt: 1,
                                    pb: 0.75,
                                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <TextField
                                    disabled={isSwitching}
                                    fullWidth
                                    inputRef={searchInputRef}
                                    size="small"
                                    placeholder="Search"
                                    variant="outlined"
                                    value={searchValue}
                                    onChange={(evt) => {
                                        setSearchValue(evt.target.value)
                                    }}
                                    onKeyDown={(evt) => {
                                        if (evt.key === 'Escape') {
                                            closeDropdown()
                                            triggerButtonRef.current?.focus()
                                            return
                                        }

                                        if (evt.key === 'Enter') {
                                            handleSearchEnter(evt)
                                            return
                                        }

                                        if (evt.key === 'Tab') {
                                            handleSearchTab(evt)
                                            return
                                        }
                                    }}
                                    inputProps={{
                                        'aria-label': 'Search organizations',
                                    }}
                                    InputProps={{
                                        sx: {
                                            typography: 'body1',
                                        },
                                        endAdornment: isSwitching ? (
                                            <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} />
                                        ) : searchValue.trim().length > 0 && filteredAuctionList.length === 1 ? (
                                            <Box
                                                sx={{
                                                    alignItems: 'center',
                                                    color: 'text.secondary',
                                                    display: 'flex',
                                                    gap: 0.5,
                                                    pr: 0.5,
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        letterSpacing: '0.04em',
                                                        textTransform: 'uppercase',
                                                    }}
                                                >
                                                    Enter
                                                </Box>
                                                <KeyboardReturnIcon fontSize="small" sx={{ opacity: 0.75 }} />
                                            </Box>
                                        ) : null,
                                    }}
                                />
                            </Box>
                            <List disablePadding role="listbox" sx={{ maxHeight: 360, overflowY: 'auto', py: 0.5 }}>
                                {filteredAuctionList.length > 0 ? (
                                    filteredAuctionList.map((auction) => (
                                        <ListItemButton
                                            disabled={isSwitching}
                                            key={auction.orgId}
                                            selected={auction.orgId === selectedAuctionId}
                                            onClick={(evt) => {
                                                void handleOrgChange(evt, auction)
                                            }}
                                            sx={{
                                                alignItems: 'center',
                                                color: 'text.secondary',
                                                display: 'flex',
                                                overflow: 'hidden',
                                                typography: 'body1',
                                                whiteSpace: 'nowrap',
                                                '&.Mui-selected': {
                                                    color: 'primary.main',
                                                },
                                            }}
                                        >
                                            <AuctionOptionContent
                                                organization={auction}
                                                isGlobalAdmin={isGlobalAdmin}
                                                isSelected={auction.orgId === selectedAuctionId}
                                            />
                                        </ListItemButton>
                                    ))
                                ) : (
                                    <Box sx={{ color: 'text.secondary', px: 2, py: 1.5 }}>
                                        No matching organizations
                                    </Box>
                                )}
                            </List>
                        </Paper>
                    </Popper>
                </FormControl>
            </Box>
        </ClickAwayListener>
    )
}

export default UserAccountSelect
